import ApiService from '../api/ApiService';
import {ThunkDispatch} from 'redux-thunk';
import {call, put} from 'redux-saga/effects';
import {AnyAction} from 'redux';
import {isString} from 'lodash';

import {pushErrorToast} from 'features/notifications/redux/actions';
import i18n from '../i18n';
import {loginTypes} from '../features/login/redux';
import {action, asyncType} from './helpers';
import {DialogCallbacks} from 'types/common';
import {IErrorMessage} from 'types/diplomat/ErrorItem';

export const getApiBody = async (resp: Response) => {
   // console.debug(resp);
   if (resp.bodyUsed) return resp.body;

   const contentType = resp.headers.get('Content-Type');
   // console.debug(contentType);
   if (!contentType || contentType?.indexOf('text') >= 0) {
      return await resp.text();
   }

   if (contentType?.indexOf('application/json') >= 0) {
      return await resp.json();
   }
};

export const getErrors = async (e: any, url?: string) => {
   console.debug('Handle api error', e, e.message, url, typeof e);
   let message =
      e.message ||
      e.error ||
      e.statusText ||
      e?.[0]?.message ||
      `${i18n.t('Get an error calling')} 
   ${url}`;
   let eBody = e.errorBody || e;
   try {
      eBody = await e.text();
      message = eBody || message;
      eBody = JSON.parse(eBody);
   } catch (bodyErr) {
      // console.debug(bodyErr);
   }
   // console.debug(eBody);

   try {
      message =
         (eBody?.errors?.length && `${eBody.errors.map((e: any) => e.message + '\n')}`) ||
         eBody.error ||
         message;
   } catch (e) {
      // console.debug(e);
   }

   return {message, error: eBody};
};

export const dispatchAsync = (
   url: string,
   type: ReturnType<typeof asyncType>,
   options?: DialogCallbacks & {getMethod?: any; data?: any; useReqDataOnSuccess?: boolean} & {
      hideErrorToast?: boolean;
      // swallowExceptions?: boolean;
   }
) => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
   try {
      await dispatch(action(type.REQUEST, options?.data));

      const method = options?.getMethod || ApiService.getSecureJson;
      const body = isString(options?.data) ? options?.data : JSON.stringify(options?.data);
      const res = await method(url, body);

      let json;
      try {
         json = await getApiBody(res);
      } catch (e) {
         console.debug('JSON is empty or has another format');
      }

      if (options?.useReqDataOnSuccess) {
         await dispatch(action(type.SUCCESS, {request: options?.data, response: json}));
      } else {
         await dispatch(action(type.SUCCESS, json));
      }

      if (options?.onSuccess) {
         await options.onSuccess(json, dispatch, res);
      }
      return json;
   } catch (e) {
      const {message, error} = await getErrors(e, url);

      await dispatch(action(type.ERROR, error));

      const errorHandled = await options?.onError?.(error, dispatch, e as Response);

      // handle 401 and 500
      if ((e as Response).status === 401) {
         await dispatch(action(loginTypes.LOGIN.ERROR, e));
      } else if (!errorHandled && !options?.hideErrorToast) {
         await dispatch(pushErrorToast(message));
      }
   }
};

export const asyncSagaConstructor = (
   url: string,
   type: ReturnType<typeof asyncType>,
   options?: DialogCallbacks & {getMethod?: any; data?: any} & {
      hideErrorToast?: boolean;
      swallowExceptions?: boolean;
   }
) =>
   function* () {
      try {
         yield put(action(type.REQUEST, options?.data));

         const body = isString(options?.data) ? options?.data : JSON.stringify(options?.data);
         const res = (yield call(
            options?.getMethod || ApiService.getSecureJson,
            url,
            body
         )) as Response;

         const json = (yield call(getApiBody, res)) as string | Object;
         if (options?.onSuccess) {
            yield call(options?.onSuccess, json);
         }

         yield put(action(type.SUCCESS, json || options?.data));

         return json;
      } catch (e) {
         const {message, error} = yield call(getErrors, e, url);
         yield put(action(type.ERROR, error));
         if (options?.onError) {
            yield call(options?.onError, error, undefined, e as Response);
         }

         //handle 401
         if ((e as Response).status === 401) {
            yield put(action(loginTypes.LOGIN.ERROR, error));
         } else if (!options?.hideErrorToast) {
            yield put(pushErrorToast(message));
         }

         if (!options?.swallowExceptions) {
            throw Object.assign(e as Error, {errorMessage: message, errorBody: error});
         }
      }
   };

export function* unhandedSagaError(e: Error) {
   console.error(e);
   yield put(pushErrorToast('Unhandled error: ' + e.message));
}

export interface SagaError extends Error {
   errorMessage: string;
   errorBody: {errors: IErrorMessage; warnings: IErrorMessage};
}
